import React, { useMemo } from 'react';
import { intl } from '../../lib';
import type { SelectProps } from './Select';
import { Select } from './Select';

/** Type of the selectable items. */
type ItemType = {
  /** gender option label. */
  label: string;
  /** gender option value. */
  value: string;
};

/**
 * Props of the {@link GenderSelect}.
 */
export type GenderSelectProps = Omit<
  SelectProps<ItemType, 'single'>,
  | 'variant'
  | 'items'
  | 'label'
  | 'itemLabelProp'
  | 'itemValueProp'
  | 'value'
  | 'onChange'
> & {
  /** If the "other" gender option should be shown. */
  genderOther: boolean;
  /** If the "unknown" gender option should be shown. */
  genderUnknown: boolean;
  /** If the "all" gender option should be shown. This is meant to be used for filter use-cases. */
  genderAll?: boolean;
  /** The value. It can either be a KeyValuePair or simply a string for the key of the selected value. */
  value: string | null | undefined;
  /**
   * The change callback.
   * @param value the selected gender value as string, or null if none is selected
   */
  onChange: (value: string | null) => void;
};

/**
 * Function to get an array of the valid selectable gender options depending on which genders are enabled.
 *
 * @param genderAll if the 'A' gender is enabled
 * @param genderUnknown if the 'U' gender is enabled
 * @param genderOther if the 'O' gender is enabled
 * @return an Array of {@link ItemType}
 */
export function getValidGenderOptions({
  genderAll,
  genderUnknown,
  genderOther
}: Required<
  Pick<GenderSelectProps, 'genderOther' | 'genderAll' | 'genderUnknown'>
>): Array<ItemType> {
  const options = [
    {
      label: intl('gender.M'),
      value: 'M'
    },
    {
      label: intl('gender.F'),
      value: 'F'
    }
  ];
  if (genderOther) {
    options.push({
      label: intl('gender.O'),
      value: 'O'
    });
  }
  if (genderUnknown) {
    options.push({
      label: intl('gender.U'),
      value: 'U'
    });
  }
  if (genderAll) {
    options.push({
      // TODO: common key
      label: intl('patientqueryparameterportlet.value.all'),
      value: 'A'
    });
  }
  return options;
}

/**
 * A simple {@link Select} component for gender selection.
 * This select element only support selection of a single gender option.
 *
 * @param properties {@link GenderSelectProps}.
 * @return the created {@link React.JSX.Element}.
 * @constructor
 */
export function GenderSelect({
  genderAll = false,
  genderUnknown = false,
  genderOther = false,
  value,
  onChange,
  ...props
}: Readonly<GenderSelectProps>): React.JSX.Element {
  const genderOptions = useMemo<ItemType[]>(
    () =>
      getValidGenderOptions({
        genderAll,
        genderOther,
        genderUnknown
      }),
    [genderAll, genderOther, genderUnknown]
  );

  return (
    <Select
      {...props}
      variant="single"
      itemLabelProp="label"
      itemValueProp="value"
      label={intl('common.gender')}
      items={genderOptions}
      value={genderOptions.find(p => p.value === value) ?? null}
      onChange={(selectedItem: typeof genderOptions[0] | null) => {
        onChange(selectedItem?.value ?? null);
      }}
    />
  );
}
